/**
 * ConfirmDialog — modal de confirmation accessible, theme dark.
 *
 * Remplace les `confirm()` natifs (8+ occurrences dans le code) qui :
 *  - cassent le theme (popup OS clair sur app dark)
 *  - ne sont pas accessibles (lecteur d'ecran limite)
 *  - ne supportent pas les variantes (danger, info...)
 *
 * Usage hook-based :
 *   const { confirm, dialog } = useConfirm();
 *   ...
 *   const ok = await confirm({
 *     title: 'Supprimer cette regle ?',
 *     message: 'Action irreversible.',
 *     confirmLabel: 'Supprimer',
 *     variant: 'danger',
 *   });
 *   if (!ok) return;
 *   ...
 *   return <>{dialog}<button onClick={handleDelete}>Delete</button></>;
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';

export type ConfirmOptions = {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'default';
};

type DialogState = (ConfirmOptions & { resolve: (ok: boolean) => void }) | null;

export function useConfirm() {
  const [state, setState] = useState<DialogState>(null);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({ ...opts, resolve });
    });
  }, []);

  const handleClose = useCallback(
    (ok: boolean) => {
      if (!state) return;
      state.resolve(ok);
      setState(null);
    },
    [state],
  );

  const dialog = state ? (
    <ConfirmDialog
      title={state.title}
      message={state.message}
      confirmLabel={state.confirmLabel}
      cancelLabel={state.cancelLabel}
      variant={state.variant}
      onConfirm={() => handleClose(true)}
      onCancel={() => handleClose(false)}
    />
  ) : null;

  return { confirm, dialog };
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  variant = 'default',
  onConfirm,
  onCancel,
}: ConfirmOptions & {
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  // Focus auto sur le bouton confirm (sauf danger : on focus cancel pour
  // eviter une suppression accidentelle au tap espace).
  useEffect(() => {
    confirmBtnRef.current?.focus();
  }, []);

  // Echap = annuler
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onCancel]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-950/80 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="tc-panel max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-4">
          {variant === 'danger' && (
            <AlertTriangle
              aria-hidden
              className="w-5 h-5 text-red-400 shrink-0 mt-0.5"
              strokeWidth={1.75}
            />
          )}
          <div className="flex-1">
            <h2
              id="confirm-title"
              className="font-display text-base text-steel-100 tracking-tight"
            >
              {title}
            </h2>
            {message && (
              <p className="text-sm text-steel-300 mt-2 leading-relaxed">{message}</p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onCancel} className="tc-btn-secondary text-2xs">
            {cancelLabel}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={onConfirm}
            className={
              variant === 'danger'
                ? 'px-4 py-2 text-2xs font-mono uppercase tracking-widest border border-red-900/80 bg-red-900/40 text-red-100 hover:bg-red-900/60 transition-colors'
                : 'tc-btn-primary'
            }
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
